/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { ICapturedTile, ITilePosition } from '@appCanvasCommon/export/ExportCapture';
import { ExportStitch, IStitchResult } from '@appCanvasCommon/export/ExportStitch';

export interface IStitchCombinerConfig {
	/**
	 * Maximum number of tiles to include in each partial stitch
	 */
	maxTilesPerStitch: number;
	/**
	 * Maximum number of output stitches to create (default: 150)
	 */
	maxOutputStitches: number;
	/**
	 * Margin to add around each partial stitch (default: 100)
	 */
	margin?: number;
	/**
	 * Background color for margin area (default: '#FFFFFF')
	 */
	backgroundColor?: string;
}
/**
 * TileCombiner takes an array of ICapturedTile[] objects and then merges them by performing partial stitches
 * The intention here is to reduce the total amount of images we potentially need to upload via the API to a more
 * sane value. So if we have 1000 tiles, we "compress" them down to say 150 by stitching slices of those tiles
 */
export class TileCombiner {
	/**
	 * Our sticher is responsible for actually stitching all of the slices of tiles together
	 * @private
	 */
	private exportStitch: ExportStitch = new ExportStitch();

	/**
	 * Takes an array of ICapturedTile[] and a config object and generates a new ICaptuedTile[] array of stitched
	 * tiles from slices of the original input array. Intention here is to turn many small tiles into larger stitched images
	 * so we do not need to upload hundreds or thousands of individual tiles to the backend
	 * @param tiles - Input of ICapturedTile[] to compress
	 * @param config - Config object to control how many total tiles we want
	 */
	public compress(tiles: ICapturedTile[], config: IStitchCombinerConfig): ICapturedTile[] {
		if (tiles.length === 0) {
			return [];
		}

		const maxOutputStitches: number = config.maxOutputStitches;
		const tilesPerStitch: number = this.calculateOptimalTilesPerStitch(
			tiles.length,
			config.maxTilesPerStitch,
			maxOutputStitches
		);

		const partialStitches: ICapturedTile[] = [];
		let stitchIndex: number = 0;

		for (let i = 0; i < tiles.length; i += tilesPerStitch) {
			const groupEnd: number = Math.min(i + tilesPerStitch, tiles.length);
			const tileGroup: ICapturedTile[] = tiles.slice(i, groupEnd);

			const stitchedTile: ICapturedTile | null = this.stitchTileGroup(tileGroup, config, stitchIndex);
			if (stitchedTile) {
				partialStitches.push(stitchedTile);
				stitchIndex++;
			}
		}

		return partialStitches;
	}
	/**
	 * Determine how many tiles we want to include in aach partial stitch
	 * @param totalTiles
	 * @param maxTilesPerStitch
	 * @param maxOutputStitches
	 * @private
	 */
	private calculateOptimalTilesPerStitch(
		totalTiles: number,
		maxTilesPerStitch: number,
		maxOutputStitches: number
	): number {
		const minTilesPerStitch: number = Math.ceil(totalTiles / maxOutputStitches);
		return Math.min(maxTilesPerStitch, Math.max(minTilesPerStitch, 1));
	}
	/**
	 * Stitches a group of tiles together
	 * @param tiles
	 * @param config
	 * @param stitchIndex
	 * @private
	 */
	private stitchTileGroup(
		tiles: ICapturedTile[],
		config: IStitchCombinerConfig,
		stitchIndex: number
	): ICapturedTile | null {
		if (tiles.length === 0) {
			return null;
		}

		let minX: number = Number.MAX_VALUE;
		let minY: number = Number.MAX_VALUE;
		let maxX: number = Number.MIN_VALUE;
		let maxY: number = Number.MIN_VALUE;

		for (const tile of tiles) {
			minX = Math.min(minX, tile.position.x);
			minY = Math.min(minY, tile.position.y);
			maxX = Math.max(maxX, tile.position.x + tile.position.width);
			maxY = Math.max(maxY, tile.position.y + tile.position.height);
		}

		const width: number = maxX - minX;
		const height: number = maxY - minY;

		const adjustedTiles: ICapturedTile[] = tiles.map((tile) => ({
			...tile,
			position: {
				...tile.position,
				x: tile.position.x - minX,
				y: tile.position.y - minY,
			},
		}));

		const stitchResult: IStitchResult = this.exportStitch.stitch(adjustedTiles, width, height, {
			margin: config.margin || 0,
			backgroundColor: config.backgroundColor,
		});

		if (!stitchResult.success || !stitchResult.canvas) {
			return null;
		}

		const position: ITilePosition = {
			x: minX,
			y: minY,
			width: stitchResult.width || width,
			height: stitchResult.height || height,
		};

		return {
			canvas: stitchResult.canvas,
			position,
			index: stitchIndex,
		};
	}
}
